import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  type FleetProviderView,
  fleetOverviewQueryKey,
  listFleetUnitsQueryKey,
  pauseSemanticsLabel,
  providerName,
  provisionUnitMutation,
  UnitCard,
  unitClassLabel,
  useFleetOverview,
} from '@/entities/fleet'
import { errorMessage } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { SelectField } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { PillTabs } from '@/shared/ui/pill-tabs'
import { SearchInput } from '@/shared/ui/search-input'
import { SlideOver } from '@/shared/ui/slide-over'
import { Spinner } from '@/shared/ui/spinner'

// GPU задан классом юнита, оператор его не выбирает: C — RTX 4090 (голосовой
// стек под MPS), L — A100 80GB (vLLM). Канон — infra CLAUDE.md § Серверы.
const GPU_BY_CLASS = { C: 'rtx-4090', L: 'a100-80gb' } as const
const GPU_LABEL = { C: 'RTX 4090', L: 'A100 80GB' } as const

export const Route = createFileRoute('/units')({
  component: FleetPage,
  staticData: { crumb: 'Флот' },
})

type Filter = 'all' | 'ready' | 'attention' | 'paused'

const ATTENTION = new Set(['creating', 'bootstrapping', 'draining', 'error'])

function FleetPage() {
  const [showDeleted, setShowDeleted] = useState(false)
  const { units, providers, isPending, isError, error, refetch } = useFleetOverview({
    includeAll: showDeleted,
  })
  const { unitId } = useParams({ strict: false })
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [provisionOpen, setProvisionOpen] = useState(false)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return units.filter((u) => {
      if (filter === 'ready' && u.status !== 'ready') return false
      if (filter === 'attention' && !ATTENTION.has(u.status)) return false
      if (filter === 'paused' && u.status !== 'paused') return false
      if (!q) return true
      return [u.id, u.gpu_model, u.provider_account_id, u.public_ip ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
  }, [units, filter, search])

  const counts = useMemo(
    () => ({
      all: units.length,
      ready: units.filter((u) => u.status === 'ready').length,
      attention: units.filter((u) => ATTENTION.has(u.status)).length,
      paused: units.filter((u) => u.status === 'paused').length,
    }),
    [units],
  )

  return (
    <>
      <PageHeader
        title="Флот"
        description="GPU-юниты инсталляции: провижн, паузы, наработка"
        actions={
          <>
            <Button
              aria-pressed={showDeleted}
              variant={showDeleted ? 'primary' : 'ghost'}
              onClick={() => setShowDeleted((v) => !v)}
            >
              <Icon name="trash" size={16} />
              Показать удалённые
            </Button>
            <Button variant="accent" onClick={() => setProvisionOpen(true)}>
              <Icon name="plus" size={18} />
              Провижн юнита
            </Button>
          </>
        }
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить флот"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          <ProvidersStrip providers={providers} />

          <div className="flex items-start gap-6">
            <div className="flex w-[400px] shrink-0 flex-col gap-3">
              <SearchInput
                placeholder="Поиск по id, GPU, провайдеру…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Поиск юнитов"
              />
              <PillTabs
                aria-label="Фильтр юнитов"
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'all', label: 'Все', count: counts.all },
                  { value: 'ready', label: 'Готовы', count: counts.ready },
                  { value: 'attention', label: 'Внимание', count: counts.attention },
                  { value: 'paused', label: 'Пауза', count: counts.paused },
                ]}
              />
              <div className="flex flex-col gap-2">
                {filtered.length === 0 && (
                  <EmptyState
                    icon="server"
                    title="Юнитов нет"
                    description="Под фильтр ничего не попало"
                  />
                )}
                {filtered.map((unit) => (
                  <UnitCard
                    key={unit.id}
                    unit={unit}
                    providerName={providerName(providers, unit.provider_account_id)}
                    active={unitId === unit.id}
                    linked={unit.status !== 'deleted'}
                  />
                ))}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
        </div>
      )}

      <ProvisionSlideOver
        open={provisionOpen}
        onClose={() => setProvisionOpen(false)}
        providers={providers.map((p) => ({ id: p.id, name: p.name }))}
      />
    </>
  )
}

/**
 * Аккаунты провайдеров: чем платим за карты и с чего клонируем юниты. Эталон
 * класса — сервер, с которого запекается золото: без него ядро не поднимет ни
 * одного юнита этого класса у этого аккаунта, и «эталон не назначен» — не
 * мелочь оформления, а причина будущего `no-stock`.
 */
function ProvidersStrip({ providers }: { providers: FleetProviderView[] }) {
  if (providers.length === 0) return null

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-title font-medium">
        Аккаунты провайдеров <span className="text-faint text-body-sm">· эталоны клона</span>
      </h2>
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 2xl:grid-cols-3">
        {providers.map((provider) => (
          <Card
            key={provider.id}
            role="group"
            aria-label={`Провайдер ${provider.name}`}
            className="flex flex-col gap-2 p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-body-sm font-medium">{provider.name}</p>
                <p className="truncate font-mono text-caption text-muted">{provider.id}</p>
              </div>
              <span className="shrink-0 text-caption text-muted">
                {pauseSemanticsLabel(provider.pause_semantics)}
              </span>
            </div>
            <dl className="flex flex-col gap-1">
              {(provider.supported_classes ?? []).map((cls) => {
                const etalon = provider.etalons?.[cls]
                return (
                  <div key={cls} className="flex items-baseline justify-between gap-3">
                    <dt className="shrink-0 text-caption text-muted">{unitClassLabel(cls)}</dt>
                    <dd
                      className={
                        etalon ? 'truncate font-mono text-caption' : 'text-caption text-warn'
                      }
                    >
                      {etalon ?? 'эталон не назначен — клонировать нечего'}
                    </dd>
                  </div>
                )
              })}
            </dl>
          </Card>
        ))}
      </div>
    </section>
  )
}

function ProvisionSlideOver({
  open,
  onClose,
  providers,
}: {
  open: boolean
  onClose: () => void
  providers: Array<{ id: string; name: string }>
}) {
  const qc = useQueryClient()
  const [providerId, setProviderId] = useState('')
  const [cls, setCls] = useState<'C' | 'L'>('L')

  const provision = useMutation({
    ...provisionUnitMutation(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fleetOverviewQueryKey() })
      void qc.invalidateQueries({ queryKey: listFleetUnitsQueryKey() })
    },
  })

  const close = () => {
    provision.reset()
    onClose()
  }

  const result = provision.data

  return (
    <SlideOver
      open={open}
      onClose={close}
      title={result ? 'Юнит создан' : 'Провижн юнита'}
      footer={
        result ? (
          <Button variant="primary" onClick={close}>
            Готово
          </Button>
        ) : (
          <>
            <Button onClick={close}>Отмена</Button>
            <Button
              variant="primary"
              pending={provision.isPending}
              disabled={!providerId}
              onClick={() =>
                provision.mutate({
                  body: {
                    provider_account_id: providerId,
                    class: cls,
                    gpu_model: GPU_BY_CLASS[cls],
                  },
                })
              }
            >
              Создать
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="flex flex-col gap-4">
          <p className="text-body-sm text-muted">
            Юнит <span className="font-medium text-app-fg">{result.data.id}</span> создан. Токены
            показываются <span className="font-medium text-warn">один раз</span> — сохрани их в
            секреты деплоя юнита.
          </p>
          <TokenReveal label="Unit token" value={result.unit_token} />
          <TokenReveal label="Engine token" value={result.engine_token} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <SelectField
            label="Провайдер"
            required
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
          >
            <option value="" disabled>
              Выбери провайдера
            </option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Класс"
            required
            value={cls}
            onChange={(e) => setCls(e.target.value as 'C' | 'L')}
          >
            <option value="C">C — голосовой (STT/TTS/EOU, SIP)</option>
            <option value="L">L — языковой (LLM)</option>
          </SelectField>
          <div className="rounded-control bg-raise px-4 py-2.5 text-body-sm text-muted">
            GPU: <span className="font-medium text-app-fg">{GPU_LABEL[cls]}</span> — предопределён
            классом
          </div>
          {provision.isError && (
            <p className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
              {errorMessage(provision.error)}
            </p>
          )}
        </div>
      )}
    </SlideOver>
  )
}

function TokenReveal({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="rounded-control bg-raise p-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-caption text-muted">{label}</span>
        <button
          type="button"
          className="flex items-center gap-1 text-caption text-accent hover:opacity-80"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            })
          }}
        >
          <Icon name={copied ? 'check' : 'copy'} size={13} />
          {copied ? 'Скопировано' : 'Копировать'}
        </button>
      </div>
      <code className="block break-all font-mono text-caption text-app-fg">{value}</code>
    </div>
  )
}
